import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Box,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    CircularProgress
} from '@mui/material';
import {
    Person,
    School,
    Work,
    Description,
    Email,
    Phone,
    Download
} from '@mui/icons-material';
import { getEmployeeById } from '../../../../api/employeeApi';

const OnboardingDetailsModal = ({ open, onClose, employeeId }) => {
    const [employee, setEmployee] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (open && employeeId) {
            fetchDetails();
        }
    }, [open, employeeId]);

    const fetchDetails = async () => {
        try {
            setLoading(true);
            const response = await getEmployeeById(employeeId);
            if (response.success) {
                setEmployee(response.data);
            }
        } catch (error) {
            console.error('Error fetching employee details:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ bgcolor: '#7522D1', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person /> Onboarding Details: {employee?.firstName} {employee?.lastName}
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                        <CircularProgress color="primary" />
                    </Box>
                ) : !employee ? (
                    <Typography color="error">Failed to load employee details.</Typography>
                ) : (
                    <Box>
                        {/* Basic Info */}
                        <Typography variant="h6" sx={{ color: '#7522D1', mb: 1, mt: 2, fontWeight: 600 }}>Personal Information</Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">Email</Typography>
                                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Email fontSize="small" /> {employee.email}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">Phone</Typography>
                                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Phone fontSize="small" /> {employee.phone || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">Father's Name</Typography>
                                <Typography variant="body1">{employee.personalInfo?.fatherName || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">Date of Birth</Typography>
                                <Typography variant="body1">{employee.personalInfo?.dob ? new Date(employee.personalInfo.dob).toLocaleDateString() : 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary">Emergency Contact</Typography>
                                <Typography variant="body1">
                                    {employee.personalInfo?.emergencyContactName} ({employee.personalInfo?.emergencyContactRelation}) - {employee.personalInfo?.emergencyContactPhone}
                                </Typography>
                            </Grid>
                        </Grid>

                        {/* Bank Details */}
                        <Typography variant="h6" sx={{ color: '#7522D1', mb: 1, mt: 3, fontWeight: 600 }}>Bank Details</Typography>
                        <Divider sx={{ mb: 2 }} />
                        {employee.bankDetails ? (
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Account Name</Typography>
                                    <Typography variant="body1">{employee.bankDetails.accountName}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Account Number</Typography>
                                    <Typography variant="body1">{employee.bankDetails.accountNumber}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">Bank Name</Typography>
                                    <Typography variant="body1">{employee.bankDetails.bankName}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Typography variant="body2" color="textSecondary">IFSC Code</Typography>
                                    <Typography variant="body1">{employee.bankDetails.ifscCode}</Typography>
                                </Grid>
                            </Grid>
                        ) : <Typography color="textSecondary">No bank details provided.</Typography>}

                        {/* Education */}
                        <Typography variant="h6" sx={{ color: '#7522D1', mb: 1, mt: 3, fontWeight: 600 }}>Education Details</Typography>
                        <Divider sx={{ mb: 2 }} />
                        {employee.education?.length > 0 ? (
                            <List dense>
                                {employee.education.map((edu, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon><School /></ListItemIcon>
                                        <ListItemText
                                            primary={`${edu.qualificationLevel} - ${edu.degree}`}
                                            secondary={`${edu.collegeName} | ${edu.yearOfCompletion} | CGPA/Percentage: ${edu.cgpaPercentage}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : <Typography color="textSecondary">No education details provided.</Typography>}

                        {/* Experience */}
                        <Typography variant="h6" sx={{ color: '#7522D1', mb: 1, mt: 3, fontWeight: 600 }}>Experience Details</Typography>
                        <Divider sx={{ mb: 2 }} />
                        {employee.experience?.length > 0 ? (
                            <List dense>
                                {employee.experience.map((exp, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon><Work /></ListItemIcon>
                                        <ListItemText
                                            primary={`${exp.designation} at ${exp.companyName}`}
                                            secondary={`${new Date(exp.fromDate).toLocaleDateString()} to ${exp.toDate ? new Date(exp.toDate).toLocaleDateString() : 'Present'}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        ) : <Typography color="textSecondary">No experience details provided.</Typography>}

                        {/* Documents */}
                        <Typography variant="h6" sx={{ color: '#7522D1', mb: 1, mt: 3, fontWeight: 600 }}>Uploaded Documents</Typography>
                        <Divider sx={{ mb: 2 }} />
                        {employee.documents?.length > 0 ? (
                            <Grid container spacing={1}>
                                {employee.documents.map((doc) => (
                                    <Grid item key={doc.id}>
                                        <Chip
                                            icon={<Description />}
                                            label={doc.documentType}
                                            onClick={() => window.open(doc.fileUrl, '_blank')}
                                            color="primary"
                                            variant="outlined"
                                            deleteIcon={<Download />}
                                            onDelete={() => window.open(doc.fileUrl, '_blank')}
                                            sx={{ m: 0.5 }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : <Typography color="textSecondary">No documents uploaded.</Typography>}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#7522D1', '&:hover': { bgcolor: '#5e1ba8' } }}>
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OnboardingDetailsModal;
